<template>
  <v-navigation-drawer
    :app="true"
    :value="drawerState"
    class="drawer_container"
    @input="$emit('updateDrawerState', $event)"
  >
    <v-btn class="close_btn" icon @click="$emit('updateDrawerState', false)">
      <v-icon>$mdiClose </v-icon>
    </v-btn>
    <v-overlay v-model="overlay" contained />
    <router-link :to="{ name: 'dashboard' }">
      <BaseImage :src="$findStatic('cosmoLogo')" class="cosmo_logo" />
    </router-link>
    <section class="my-2">
      <div v-if="isLoadingOrganizations">
        <BaseSkeletonLoader type="text" class="mb-4" />
        <BaseSkeletonLoader type="chip" class="mb-4" />
        <BaseSkeletonLoader type="chip" />
      </div>
      <div v-else-if="organizations.length">
        <BaseDivider color="primary" />
        <span
          v-if="organizations.length > 1"
          class="text-caption font-weight-regular text-uppercase primary--text mt-2"
          v-text="$tc('organization.organization', organizations.length)"
        />
        <BaseList>
          <BaseListGroup
            v-for="(organization, index) in organizations"
            :key="index"
            color="primary"
            :value="$route.params.organizationId == organization.id"
            :opened="organizations.length === 1"
            class="drawer"
            :class="{
              'custom__list-group--active':
                $route.params.organizationId == organization.id,
            }"
          >
            <template #activator>
              <BaseListItemContent>
                <BaseListItemTitle>
                  <div
                    class="primary--text sub_title"
                    v-text="organization.name"
                  />
                </BaseListItemTitle>
              </BaseListItemContent>
            </template>
            <BaseListItem
              :to="{
                name: 'ORGANIZATION_DASHBOARD',
                params: { organizationId: organization.id },
              }"
            >
              <BaseListItemContent>
                <BaseListItemTitle>
                  <div
                    class="primary--text text_sm ml-4"
                    v-text="$tc('organization.dashboard', 2)"
                  />
                </BaseListItemTitle>
              </BaseListItemContent>
            </BaseListItem>
            <v-list-group
              v-if="isManager(organization.id)"
              class="list_group_container"
              :value="isAdminUrl"
              sub-group
              prepend-icon
            >
              <template #activator>
                <v-list-item-content class="group_content">
                  <v-list-item-title>
                    <div
                      class="group_title primary--text text_sm"
                      v-text="$t('utils.configuration')"
                    />
                  </v-list-item-title>
                </v-list-item-content>
              </template>
              <template #appendIcon>
                <v-icon> $expand </v-icon>
              </template>
              <v-list-item
                :to="{
                  name: 'ORGANIZATION_SETTINGS',
                  params: { organizationId: organization.id },
                }"
              >
                <v-list-item-title>
                  <div
                    class="group_list_item primary--text text_sm"
                    v-text="$t('utils.general')"
                  />
                </v-list-item-title>
              </v-list-item>
              <v-list-item
                :to="{
                  name: 'COLLABORATORS_MANAGEMENT',
                  params: { organizationId: organization.id },
                }"
              >
                <v-list-item-title>
                  <div
                    class="group_list_item primary--text text_sm"
                    v-text="$t('utils.collaborators')"
                  />
                </v-list-item-title>
              </v-list-item>
              <v-list-item
                :to="{
                  name: 'WORKFLOW_LIST',
                  params: { organizationId: organization.id },
                }"
              >
                <v-list-item-title>
                  <div
                    class="group_list_item primary--text text_sm"
                    v-text="$t('utils.workflows')"
                  />
                </v-list-item-title>
              </v-list-item>
              <v-list-item
                :to="{
                  name: 'COURSE_LIST',
                  params: { organizationId: organization.id },
                }"
              >
                <v-list-item-title>
                  <div
                    class="group_list_item primary--text text_sm"
                    v-text="$tc('utils.course', 3)"
                  />
                </v-list-item-title>
              </v-list-item>
              <v-list-item
                :to="{
                  name: 'CONTROL_LIST',
                  params: { organizationId: organization.id },
                }"
              >
                <v-list-item-title>
                  <div
                    class="group_list_item primary--text text_sm"
                    v-text="$t('controls.title')"
                  />
                </v-list-item-title>
              </v-list-item>
              <v-list-item
                :to="{
                  name: 'QUESTION_TEMPLATE',
                  params: { organizationId: organization.id },
                }"
              >
                <v-list-item-title>
                  <div
                    class="group_list_item primary--text text_sm"
                    v-text="$t('utils.question_template')"
                  />
                </v-list-item-title>
              </v-list-item>
              <v-list-item
                :to="{
                  name: 'DIRECTORY',
                  params: { organizationId: organization.id },
                }"
              >
                <v-list-item-title>
                  <div
                    class="group_list_item primary--text text_sm"
                    v-text="$t('utils.directory')"
                  />
                </v-list-item-title>
              </v-list-item>
              <v-list-item
                :to="{
                  name: 'IMPORTS',
                  params: { organizationId: organization.id },
                }"
              >
                <v-list-item-title>
                  <div
                    class="group_list_item primary--text text_sm"
                    v-text="$t('utils.imports')"
                  />
                </v-list-item-title>
              </v-list-item>
              <v-list-item
                :to="{
                  name: 'DOCX_TEMPLATE',
                  params: { organizationId: organization.id },
                }"
              >
                <v-list-item-title>
                  <div
                    class="group_list_item primary--text text_sm"
                    v-text="$t('utils.docx_templates')"
                  />
                </v-list-item-title>
              </v-list-item>
              <v-list-item
                :to="{
                  name: 'OFFER_TEMPLATE',
                  params: { organizationId: organization.id },
                }"
              >
                <v-list-item-title>
                  <div
                    class="group_list_item primary--text text_sm"
                    v-text="$t('utils.offer_templates')"
                  />
                </v-list-item-title>
              </v-list-item>
            </v-list-group>
            <BaseListItem
              :to="{
                name: 'clientList',
                params: { organizationId: organization.id },
              }"
            >
              <BaseListItemContent>
                <BaseListItemTitle>
                  <div
                    class="primary--text text_sm ml-4"
                    v-text="$tc('organization.client', 2)"
                  />
                </BaseListItemTitle>
              </BaseListItemContent>
            </BaseListItem>
            <BaseListItem
              :to="{
                name: 'campaignList',
                params: { organizationId: organization.id },
              }"
            >
              <BaseListItemContent>
                <BaseListItemTitle>
                  <div
                    class="primary--text text_sm ml-4"
                    v-text="$t('utils.campaign')"
                  />
                </BaseListItemTitle>
              </BaseListItemContent>
            </BaseListItem>
            <BaseListItem
              :to="{
                name: 'folderList',
                params: { organizationId: organization.id },
              }"
            >
              <BaseListItemContent>
                <BaseListItemTitle>
                  <div
                    class="primary--text text_sm ml-4"
                    v-text="$tc('organization.folder', 2)"
                  />
                </BaseListItemTitle>
              </BaseListItemContent>
            </BaseListItem>
            <v-list-group
              v-model="isWorkflowsCollapsed"
              sub-group
              prepend-icon
              @change="
                handleListGroupCollapsed(isWorkflowsCollapsed, organization.id)
              "
            >
              <template #activator>
                <v-list-item-content class="group_content">
                  <v-list-item-title>
                    <div
                      class="group_title primary--text text_sm"
                      v-text="$t('utils.workflows')"
                    />
                  </v-list-item-title>
                </v-list-item-content>
              </template>
              <template #appendIcon>
                <v-icon> $expand </v-icon>
              </template>
              <template v-if="fetchingWorkflow">
                <v-list-item v-for="value in 3" :key="`sl-${value}`">
                  <v-list-item-title>
                    <v-skeleton-loader class="skeleton" type="text" />
                  </v-list-item-title>
                </v-list-item>
              </template>
              <template v-else>
                <v-list-item
                  v-for="(workflow, wi) in workflows"
                  :key="wi"
                  :to="{
                    name: 'WORKFLOW_FOLDER',
                    params: {
                      organizationId: organization.id,
                      workflowId: `${workflow.id}`,
                    },
                  }"
                >
                  <v-list-item-title>
                    <div
                      class="group_list_item primary--text text_sm"
                      v-text="workflow.name"
                    />
                  </v-list-item-title>
                </v-list-item>
              </template>
            </v-list-group>
          </BaseListGroup>
        </BaseList>
      </div>
    </section>
    <BaseButton
      v-if="user && user.canCreateOrganization"
      class="create_btn mt-auto"
      @click.stop="createOrga = true"
    >
      <BaseIcon icon="$mdiPlus" small />
      {{ $t("organization.createOrganization") }}</BaseButton
    >
    <OrganizationCreateDialog v-if="createOrga" @close="createOrga = false" />
  </v-navigation-drawer>
</template>

<script>
import { mapState } from "vuex";
import { fetchOrganizationWorkflows } from "@/modules/Admin/Services/workflow.service";
import OrganizationCreateDialog from "@/modules/Admin/Components/OrganizationCreateDialog.vue";

export default {
  name: "TheDrawer",
  components: {
    OrganizationCreateDialog,
  },
  props: {
    drawerState: {
      type: Boolean,
    },
  },

  data() {
    return {
      isWorkflowsCollapsed: true,
      fetchingWorkflow: false,
      workflows: [],
      createOrga: false,
    };
  },
  computed: {
    ...mapState({
      organizations: (state) => state.organization.organizations,
      isLoadingOrganizations: (state) =>
        state.organization.isLoadingOrganizations,
      user: (state) => state.account.user,
      overlay: (state) => state.snackbar.overlay,
    }),
    isAdminUrl() {
      if (!this.$route.matched?.length) return false;
      return this.$route.matched.some((route) => {
        return route.name == "CONFIGURATION";
      });
    },
  },
  created() {
    this.initializeUser();
  },
  methods: {
    async initializeUser() {
      try {
        await this.$store.dispatch("account/fetchUser");
        if (!this.organizations || this.organizations.length === 0) {
          this.$router.push({
            name: "account",
          });
        }
      } catch (error) {
        this.$router.push({
          name: "Login",
          query: {
            next: this.$route.fullPath,
          },
        });
      }
    },
    isManager(organizationId) {
      return this.user ? this.user.isManager(organizationId) : false;
    },
    handleListGroupCollapsed(collapsed, organizationId) {
      if (collapsed) return;
      this.fetchWorkflows(organizationId);
    },
    async fetchWorkflows(organizationId) {
      this.fetchingWorkflow = true;
      try {
        this.workflows = [];
        let page = 1;
        while (page) {
          const res = await fetchOrganizationWorkflows(
            organizationId,
            page,
            true
          );
          if (!res) return;
          this.workflows = [
            ...this.workflows,
            ...res.data.results.filter((wf) => wf.state !== "CLOSED"),
          ];
          page = res.data.next;
        }
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
      this.fetchingWorkflow = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.drawer_container {
  padding: 24px;
  .close_btn {
    position: absolute;
    top: 10px;
    right: 10px;
  }
}
.drawer >>> .v-list-group__items {
  background-color: white !important;
}
.list_group_container {
  background: #e6eff4;
  .group_list_item {
    padding-left: 8px;
  }
  .v-list-item {
    background: #e6eff4;
  }
}
::v-deep .v-navigation-drawer__content {
  display: flex;
  flex-direction: column;
}
.create_btn {
  &.v-btn {
    background: #2536cc !important;
    ::v-deep .btn_label {
      color: #fff !important;
    }
  }
}
.cosmo_logo {
  margin: auto;
  width: 64px;
  height: 64px;
}
</style>
